// Everything the visitor reads that depends on their answers. Pure, so
// the wording rules are tested: a name is only ever the one the visitor
// typed (never a placeholder), and every phone line comes from the
// situation, not the page.

import {
  FIRST_CALL_PHONE_HREF,
  FIRST_CALL_PHONE_LABEL,
  SUPPORT_PHONE_HREF,
  SUPPORT_PHONE_LABEL,
} from "constants/phones";

import type { LeadLandingCard } from "components/LeadLanding";
import {
  consentDisclosure,
  type ConsentDisclosure,
} from "helpers/consent-disclosure";

import type { Answers, WhereType } from "./answers";
import { quotePath } from "./destinations";
import {
  isBlockingSegment,
  isAtNeedLead,
  needsSubmitterName,
  type LeadKind,
  type QuestionStepId,
  type Segment,
} from "./flow";
import type { GrantMethod, LandingRecord, LeadResult } from "./submit";

export type CallLine = { href: string; label: string };

/** The number a family should call: the around-the-clock first-call line
 * when someone has died or is expected to, the pre-need line otherwise. */
export function callLineFor(kind: LeadKind): CallLine {
  return kind === "planning"
    ? { href: SUPPORT_PHONE_HREF, label: SUPPORT_PHONE_LABEL }
    : { href: FIRST_CALL_PHONE_HREF, label: FIRST_CALL_PHONE_LABEL };
}

export interface StepCopy {
  kicker: string;
  titleA: string;
  titleB: string;
  blurb: string;
}

function lovedOneFirstName(a: Answers): string | null {
  const name =
    a.situation === "death_occurred"
      ? a.decedent.firstName
      : a.lovedOne.firstName;
  return name.trim() === "" ? null : name.trim();
}

function possessive(name: string | null, fallback: string): string {
  return name === null ? fallback : `${name}’s`;
}

export function stepCopy(a: Answers, step: QuestionStepId): StepCopy {
  const first = lovedOneFirstName(a);
  const who = first ?? "your loved one";
  const both = a.planningFor === "both";
  switch (step) {
    case "start":
      return {
        kicker: "Get started",
        titleA: "How can we",
        titleB: "help today?",
        blurb: "Answer a few questions and we’ll take it from there.",
      };
    case "who-passed":
      return {
        kicker: "Arrangements",
        titleA: "What is your loved one’s",
        titleB: "name?",
        blurb: "",
      };
    case "where":
      return {
        kicker: "Arrangements",
        titleA: "Where is",
        titleB: `${who} right now?`,
        blurb: "",
      };
    case "relation":
    case "loved-relation":
      return {
        kicker: step === "relation" ? "Arrangements" : "Your loved one",
        titleA: "How are you related to",
        titleB: `${who}?`,
        blurb: `I’m ${possessive(first, "your loved one’s")}…`,
      };
    case "ready":
      return {
        kicker: "Arrangements",
        titleA: "Are you ready for us to bring",
        titleB: `${who} into our care?`,
        blurb: "",
      };
    case "preplan":
      return {
        kicker: "Arrangements",
        titleA: `Did ${who}`,
        titleB: "pre-plan with After?",
        blurb: "If they did, we’ll connect their plan to these arrangements.",
      };
    case "planning-for":
      return {
        kicker: "Planning ahead",
        titleA: "Who are you",
        titleB: "planning for?",
        blurb: "",
      };
    case "you":
      return {
        kicker: "About you",
        titleA: both ? "Let’s start with" : "Tell us",
        titleB: both ? "you" : "about yourself",
        blurb: "We use these to price your plan.",
      };
    case "you-health":
      return {
        kicker: "About you",
        titleA: "Is your life expectancy",
        titleB: "12 months or less, or are you on hospice care?",
        blurb: "",
      };
    case "loved":
      return {
        kicker: both ? "Now your loved one" : "Your loved one",
        titleA: "What’s your loved one’s",
        titleB: "name?",
        blurb: "",
      };
    case "loved-health":
      return {
        kicker: "Your loved one",
        titleA: `Is ${possessive(first, "your loved one’s")} life expectancy`,
        titleB: "12 months or less, or are they on hospice care?",
        blurb: "",
      };
    case "loved-details":
      return {
        kicker: "Your loved one",
        titleA: "A little about",
        titleB: first ?? "your loved one",
        blurb:
          a.lovedOne.hospice === true
            ? "This helps our care team prepare."
            : `We use these to price ${possessive(first, "your loved one’s")} plan.`,
      };
    case "contact":
      return contactCopy(a);
  }
}

function contactCopy(a: Answers): StepCopy {
  const named = needsSubmitterName(a);
  const atNeed = isAtNeedLead(a);
  let blurb = "We’ll send your quote here. We never share your information.";
  if (atNeed) {
    blurb = "So our care team can reach you. We never share your information.";
  }
  return {
    kicker: "Last step",
    titleA: named ? "Now" : "Where can we",
    titleB: named ? "about you" : "reach you?",
    blurb,
  };
}

/** The disclosure under the contact step's Continue: Terms and Privacy
 * Policy are linked between `lead` and `tail`. A signed-in submitter
 * already has an account, so the sentence does not claim to create one. */
export function contactDisclosure(signedIn: boolean): ConsentDisclosure {
  return consentDisclosure({
    action: "Continue",
    hasAccount: signedIn,
    texting: { kind: "provided" },
  });
}

const WHERE_PHRASE: Record<WhereType, string> = {
  hospital: "hospital",
  private_residence: "private residence",
  care_facility: "care facility",
  medical_examiner: "medical examiner’s office",
  other: "location you shared",
};

export interface LandingStep {
  title: string;
  body: string;
}

export interface LandingCopy {
  kicker: string;
  titleA: string;
  titleB: string;
  body: string;
  steps: LandingStep[];
}

function fullName(person: { firstName: string; lastName: string }): string {
  return `${person.firstName.trim()} ${person.lastName.trim()}`.trim();
}

function submitterFirstName(a: Answers): string {
  return needsSubmitterName(a)
    ? a.submitter.firstName.trim()
    : a.self.firstName.trim();
}

function greeting(a: Answers): string {
  const name = submitterFirstName(a);
  return name === "" ? "We’re here for you." : `We’re here for you, ${name}.`;
}

function arrangementsCopy(a: Answers, line: CallLine): LandingCopy {
  const decedent = fullName(a.decedent);
  const first = a.decedent.firstName.trim();
  const where = a.where.type ? WHERE_PHRASE[a.where.type] : "place they are";
  const ready = a.readyForCare === true;
  return {
    kicker: "Arrangements",
    titleA: "Caring for",
    titleB: decedent,
    body: ready
      ? `${greeting(a)} A care specialist will call you as soon as possible from ${line.label}.`
      : `${greeting(a)} Take the time you need. When you’re ready, call us or tell us here.`,
    steps: ready
      ? [
          {
            title: "We call you",
            body: "To talk through next steps and answer your questions.",
          },
          {
            title: `We bring ${first} into our care`,
            body: `From the ${where}, once we have talked with you.`,
          },
          {
            title: "Finish arrangements online",
            body: "Paperwork, choices, and signatures in your account, at your pace.",
          },
        ]
      : [
          {
            title: "Call when you’re ready",
            body: `Day or night, ${line.label}.`,
          },
          {
            title: "Look around your account",
            body: "See what arrangements involve and what they cost.",
          },
          {
            title: "We’ll check in gently",
            body: "We won’t push. Reply to us whenever you’re ready.",
          },
        ],
  };
}

function selfPlanned(a: Answers): boolean {
  return a.planningFor === "self" || a.planningFor === "both";
}

function lovedOnePlanned(a: Answers): boolean {
  return a.planningFor === "loved_one" || a.planningFor === "both";
}

/** Who the imminent plan is for, as the landing names them. */
function imminentSubject(a: Answers): string {
  const selfImminent = selfPlanned(a) && a.self.hospice === true;
  const lovedImminent = lovedOnePlanned(a) && a.lovedOne.hospice === true;
  if (selfImminent && lovedImminent) return "you both";
  return selfImminent ? "you" : (lovedOneFirstName(a) ?? "your loved one");
}

function policySubject(a: Answers): string {
  const selfPolicy = selfPlanned(a) && a.self.hospice === false;
  return selfPolicy ? "you" : (lovedOneFirstName(a) ?? "your loved one");
}

function imminentOwner(subject: string): string {
  if (subject === "you" || subject === "you both") return "your";
  return subject === "your loved one" ? "your loved one’s" : `${subject}’s`;
}

function imminentReason(subject: string): string {
  const need = "in hospice care or may need us soon";
  if (subject === "you") return `you’re ${need}`;
  if (subject === "you both") return `you’re both ${need}`;
  return `${subject} is ${need}`;
}

function imminentCopy(a: Answers, result: LeadResult): LandingCopy {
  const hasPolicy = result.records.some((record) => record.type === "policy");
  const subject = imminentSubject(a);
  const quoteLine = hasPolicy
    ? `A quote is ready for ${policySubject(a)}. `
    : "";
  return {
    kicker: "",
    titleA: "We’ve noted",
    titleB: `${imminentOwner(subject)} details`,
    body: `${quoteLine}Because ${imminentReason(subject)}, a care specialist will call to talk through the options.`,
    steps: [],
  };
}

function callUsCopy(result: LeadResult, line: CallLine): LandingCopy {
  const body = result.acknowledged
    ? `We have your request and our team has been alerted. The quickest way forward is a call: ${line.label}.`
    : `The quickest way forward is a call: ${line.label}. We are here around the clock.`;
  return {
    kicker: "Next step",
    titleA: "Let’s talk",
    titleB: "by phone",
    body,
    steps: [],
  };
}

/** What `/start/done` says for a submission's result. The wording asks
 * the family to call and promises no response time. */
export function landingCopy(result: LeadResult, a: Answers): LandingCopy {
  const line = callLineFor(result.leadKind);
  switch (result.variant) {
    case "degraded":
      return {
        kicker: "Next step",
        titleA: "Please",
        titleB: "call us now",
        body: `We couldn’t send your details just now. Call us so we can help right away: ${line.label}.`,
        steps: [],
      };
    case "already_received":
      return {
        kicker: "Already with us",
        titleA: "We already have",
        titleB: "this request",
        body: "Check your email, or give us a call and we will pick it up with you.",
        steps: [],
      };
    case "call_us":
      return callUsCopy(result, line);
    case "case_received":
      return result.leadKind === "death"
        ? arrangementsCopy(a, line)
        : imminentCopy(a, result);
  }
}

export const OTHER_OPTIONS_COPY = {
  kicker: "Service area",
  titleA: "We’re not in",
  titleB: "that area yet",
  blurb:
    "We arrange cremations in a handful of states today, and we’re adding more. Here’s what we can still do.",
  note: "We’ll email you the moment we open in your area.",
};

/** The service-area interstitial's heading and explanation. */
export function outOfAreaCopy(): StepCopy {
  return {
    kicker: "Service area",
    titleA: "We’re not in your area",
    titleB: "just yet",
    blurb:
      "We’re working hard to expand our services to more communities. If you think the ZIP code may be wrong, you can change it.",
  };
}

/** The call line the out-of-area screen offers: only an at-need ZIP, which
 * ends the walk there, gets one. */
export function outOfAreaCallLine(segment: Segment): CallLine | null {
  return isBlockingSegment(segment) ? callLineFor("death") : null;
}

function subjectName(a: Answers, subject: LandingSubject): string {
  if (subject === "self") return fullName(a.self);
  if (subject === "loved_one") return fullName(a.lovedOne);
  return fullName(a.decedent);
}

type LandingSubject = LandingRecord["for"];

/** The plan cards for what a submission created: a policy quote to
 * continue, or a case quote. A record still waiting on the visitor to
 * confirm has no quote and no card. */
export function landingCards(
  result: LeadResult,
  a: Answers,
): LeadLandingCard[] {
  return result.records.flatMap((record): LeadLandingCard[] => {
    const quote = result.quotes.find(
      (candidate) =>
        candidate.for === record.for && candidate.type === record.type,
    );
    if (quote === undefined) return [];
    const title = subjectName(a, record.for);
    const link = {
      to: quotePath(quote.quoteId, quote.eventId),
      label:
        record.type === "policy" ? "Continue to the quote" : "View your quote",
    };
    if (record.type === "policy") {
      return [
        {
          kicker: "Quote ready",
          pillLabel: "Quote",
          title,
          sub: "Pre-planning quote \u00b7 About 10 minutes to finish",
          link,
        },
      ];
    }
    return [
      {
        kicker: "Your quote",
        pillLabel: "Quote",
        title,
        sub: "A quote you can review any time",
        link,
      },
    ];
  });
}

export interface ConfirmAction {
  method: GrantMethod;
  label: string;
}

export interface ConfirmPromptCopy {
  title: string;
  /** The sentence about how the confirmation arrives. */
  delivery: string;
  /** One action per method the grant offers, the texted code first. */
  actions: ConfirmAction[];
}

type PromptVerification = NonNullable<LeadResult["verification"]>;

function confirmNoun(offersPhone: boolean, offersEmail: boolean): string {
  if (offersPhone && offersEmail) return "email or phone";
  return offersPhone ? "phone" : "email";
}

function confirmDelivery(
  v: PromptVerification,
  offersPhone: boolean,
  offersEmail: boolean,
): string {
  if (offersPhone && offersEmail) {
    return "We’ll text a code or email a sign-in link.";
  }
  if (offersPhone) {
    return v.phoneDisplay
      ? `We’ll text a code to ${v.phoneDisplay}.`
      : "We’ll text you a code.";
  }
  return v.emailDisplay
    ? `We’ll email a sign-in link to ${v.emailDisplay}.`
    : "We’ll email you a sign-in link.";
}

/** What the confirm prompt says and offers for a login grant. It offers
 * exactly the methods the grant lists, never a texted code because a phone
 * is on file: null when the grant lists none. */
export function confirmPromptCopy(
  verification: PromptVerification,
): ConfirmPromptCopy | null {
  const offersPhone = verification.methods.includes("sms_otp");
  const offersEmail = verification.methods.includes("email_link");
  if (!offersPhone && !offersEmail) return null;
  const actions: ConfirmAction[] = [];
  if (offersPhone) actions.push({ method: "sms_otp", label: "Text me a code" });
  if (offersEmail) {
    actions.push({ method: "email_link", label: "Email me a link" });
  }
  return {
    title: `Confirm your ${confirmNoun(offersPhone, offersEmail)} to see your plan`,
    delivery: confirmDelivery(verification, offersPhone, offersEmail),
    actions,
  };
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** The people the stored submission was for, as the visitor typed them:
 * the loved one, the decedent, or the insured. Each appears once. */
function pickUpNames(result: LeadResult): string[] {
  const names = result.records
    .map((record) => subjectName(result.landingAnswers, record.for))
    .filter((name) => name !== "");
  return names.filter((name, index) => names.indexOf(name) === index);
}

/** What confirming opens: the arrangements for a death, else the plans. */
function pickUpOpens(result: LeadResult, named: number): string {
  if (result.records.some((record) => record.for === "decedent")) {
    return "the arrangements";
  }
  if (result.records.every((record) => record.for === "self")) {
    return "your plan";
  }
  return named > 1 ? "their plans" : "their plan";
}

/** The pick-up card the first screen offers while a stored submission still
 * awaits confirmation: who it is for, and what confirming opens. */
export function pickUpCopy(result: LeadResult): {
  title: string;
  body: string;
  button: string;
} {
  const names = pickUpNames(result);
  const plan = pickUpOpens(result, names.length);
  const methods = result.verification?.methods ?? [];
  const offersPhone = methods.includes("sms_otp");
  const offersEmail = methods.includes("email_link");
  const confirm =
    offersPhone || offersEmail
      ? `Confirm your ${confirmNoun(offersPhone, offersEmail)}`
      : "Confirm";
  return {
    title:
      names.length > 0
        ? `Pick up for ${joinNames(names)}`
        : "Pick up where you left off",
    body: `${confirm} to see ${plan}.`,
    button: "Confirm",
  };
}

export const EXPIRED_COPY = {
  kicker: "Next step",
  titleA: "This has",
  titleB: "expired",
  body: "Requests that aren’t confirmed within a few days close on their own. Start again and we’ll walk you through it.",
  button: "Start again",
};

export const CLOSED_COPY = {
  kicker: "Next step",
  titleA: "Please",
  titleB: "call us",
  body: "We can’t continue this request online. Call us and we’ll pick it up with you.",
};

export const RETRY_CONFIRM_COPY = {
  body: "We couldn’t get your confirmation ready just now.",
  button: "Try again",
};

export const CALL_PROMPT_TEXT = "If you’re ready, call us now";

export type ContactError = "unavailable" | "rate_limited" | "unexpected";

const CONTACT_ERROR_MESSAGE: Record<ContactError, string> = {
  unavailable:
    "We couldn’t send your request just now. Please try again, or call us.",
  rate_limited:
    "That didn't go through. Please wait a moment and try again, or call us.",
  unexpected: "Something went wrong. Please try again, or call us.",
};

export function contactErrorMessage(error: ContactError): string {
  return CONTACT_ERROR_MESSAGE[error];
}
